// Звуки по ТЗ (docs/spec/03): kick, goal, save, post, whistle, crowd, victory, defeat.
// Синтез Web Audio — без файлов и лицензий. AudioContext создаётся при первом звуке после тапа
// (браузеры не дают играть звук без жеста пользователя). Выключатель — в localStorage (настройка устройства).

export type Sfx = 'kick' | 'goal' | 'save' | 'post' | 'miss' | 'whistle' | 'victory' | 'defeat';

const STORAGE_KEY = 'pc.sound';
let ctx: AudioContext | null = null;
/** выбор в этой сессии (если localStorage недоступен) */
let memory: boolean | null = null;

export function soundEnabled(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) !== 'off';
  } catch {
    return true;
  }
}

export function setSoundEnabled(on: boolean): void {
  try {
    localStorage.setItem(STORAGE_KEY, on ? 'on' : 'off');
  } catch {
    // приватный режим — настройка не сохранится, звук всё равно переключится до перезапуска
  }
  memory = on;
}
const enabled = () => memory ?? soundEnabled();

function audio(): AudioContext | null {
  if (typeof window === 'undefined' || !('AudioContext' in window)) return null;
  ctx ??= new AudioContext();
  if (ctx.state === 'suspended') void ctx.resume();
  return ctx;
}

function tone(
  a: AudioContext,
  freq: number,
  start: number,
  dur: number,
  type: OscillatorType,
  gain = 0.2,
  endFreq?: number,
) {
  const osc = a.createOscillator();
  const g = a.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, start);
  if (endFreq) osc.frequency.exponentialRampToValueAtTime(endFreq, start + dur);
  g.gain.setValueAtTime(gain, start);
  g.gain.exponentialRampToValueAtTime(0.0001, start + dur);
  osc.connect(g).connect(a.destination);
  osc.start(start);
  osc.stop(start + dur + 0.02);
}

/** Шум (трибуны, удар): белый шум через полосовой фильтр. */
function noise(a: AudioContext, start: number, dur: number, freq: number, gain = 0.25) {
  const buffer = a.createBuffer(1, Math.ceil(a.sampleRate * dur), a.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;
  const src = a.createBufferSource();
  src.buffer = buffer;
  const filter = a.createBiquadFilter();
  filter.type = 'bandpass';
  filter.frequency.value = freq;
  const g = a.createGain();
  g.gain.setValueAtTime(0.0001, start);
  g.gain.exponentialRampToValueAtTime(gain, start + Math.min(0.15, dur / 3));
  g.gain.exponentialRampToValueAtTime(0.0001, start + dur);
  src.connect(filter).connect(g).connect(a.destination);
  src.start(start);
}

export function play(sfx: Sfx): void {
  if (!enabled()) return;
  try {
    const a = audio();
    if (!a) return;
    const t = a.currentTime + 0.01;
    switch (sfx) {
      case 'kick':
        tone(a, 160, t, 0.12, 'sine', 0.5, 50);
        noise(a, t, 0.08, 1200, 0.15);
        break;
      case 'goal':
        noise(a, t, 1.6, 900, 0.3); // трибуны
        tone(a, 523, t, 0.15, 'triangle', 0.15);
        tone(a, 784, t + 0.15, 0.3, 'triangle', 0.15);
        break;
      case 'save':
        tone(a, 120, t, 0.18, 'square', 0.18, 70);
        noise(a, t + 0.05, 0.8, 500, 0.12);
        break;
      case 'post':
        tone(a, 1760, t, 0.6, 'sine', 0.15, 1700);
        tone(a, 2637, t, 0.4, 'sine', 0.06);
        break;
      case 'miss':
        noise(a, t, 0.7, 400, 0.12);
        break;
      case 'whistle':
        tone(a, 2400, t, 0.25, 'sine', 0.12);
        tone(a, 2400, t + 0.32, 0.45, 'sine', 0.12);
        break;
      case 'victory':
        [523, 659, 784, 1047].forEach((f, i) => tone(a, f, t + i * 0.13, 0.35, 'triangle', 0.18));
        noise(a, t, 2, 900, 0.2);
        break;
      case 'defeat':
        [392, 330, 262].forEach((f, i) => tone(a, f, t + i * 0.22, 0.4, 'sine', 0.15));
        break;
    }
  } catch {
    // нет звука — не страшно
  }
}
